import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, Subscription, switchMap } from 'rxjs';
import { filter, finalize, map, tap } from 'rxjs/operators';

import { MarkFormAsTouchedIfInvalid } from 'tiime-expert-utils';
import {
  TiimeAlertModule,
  TiimeButtonModule,
  TiimeCardModule,
  TiimeDialogModule,
  TiimeIconModule,
  TiimeInputContainerModule,
  TiimeOptionModule,
  TiimeSelectModule,
  TiimeSlideToggleModule,
  TiimeSnackbarService,
  TiimeTooltipModule,
  TiimeTwocolorsIconModule
} from 'tiime-material';

import { SendMailDialogBase } from '@bases/send-mail-dialog/send-mail-dialog.base';
import { GIFileType } from '@enums/gi-file-type.enum';
import { SendContractType } from '@enums/send-contract-type.enum';
import { SendContractForm } from '@forms/send-contract-form';
import { ContractApiService } from '@gi/api/services/contract-api.service';
import { ContractEmailTemplateApiService } from '@gi/api/services/contract-email-template-api.service';
import { ContractMailTemplateType } from '@gi/enums/contract-mail-template-type.enum';
import { ContractEmailAttachmentForm } from '@gi/forms/contract-email-attachment-form';
import { ContractEmailContentTemplate } from '@gi/models/contract-email-content-template';
import { GiEmailAttachment } from '@gi/models/gi-email-attachment';
import { GiEmailTemplate } from '@gi/models/gi-email-template';
import { FormHelper } from '@helpers/form.helper';
import { CodeAndLabel } from '@models/code-and-label';
import { EmailFrom } from '@models/email-from';
import { GIFile } from '@models/gi-file';
import { SmsTemplate } from '@models/sms-template';
import { GICachedDataApiService } from '@modules/domains/gi/api/services/gi-cached-data-api.service';
import { MultipleAttachmentsSelectorComponent } from '@shared-components/multiple-attachments-selector/multiple-attachments-selector.component';
import { SendContractDialogData } from '@shared-dialogs/send-contract-dialog/send-contract-dialog-data';
import { EmailFormComponent } from '@shared-forms/email-form/email-form.component';
import { SmsFormComponent } from '@shared-forms/sms-form/sms-form.component';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-send-contract-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TiimeDialogModule,
    TiimeIconModule,
    TiimeTooltipModule,
    TiimeTwocolorsIconModule,
    TiimeButtonModule,
    TiimeCardModule,
    TiimeAlertModule,
    TiimeSlideToggleModule,
    EmailFormComponent,
    SmsFormComponent,
    MultipleAttachmentsSelectorComponent,
    TiimeInputContainerModule,
    TiimeSelectModule,
    TiimeOptionModule
  ],
  templateUrl: './send-contract-dialog.component.html',
  styleUrls: ['./send-contract-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendContractDialogComponent extends SendMailDialogBase<SendContractDialogComponent> implements OnInit {
  readonly contractMailTemplates: WritableSignal<ContractEmailContentTemplate[]> = signal<
    ContractEmailContentTemplate[]
  >([]);
  readonly sendContractForm: SendContractForm = new SendContractForm();
  readonly contractFileTypes$: Observable<CodeAndLabel<GIFileType>[]> = this.gICachedDataApiService.getFileTypes();

  readonly SendContractType: typeof SendContractType = SendContractType;

  private readonly subscription: Subscription = new Subscription();

  constructor(
    protected dialogRef: MatDialogRef<SendContractDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SendContractDialogData,
    private readonly contractApiService: ContractApiService,
    private readonly contractEmailTemplateApiService: ContractEmailTemplateApiService,
    private readonly snackbarService: TiimeSnackbarService,
    private readonly cdr: ChangeDetectorRef,
    private readonly gICachedDataApiService: GICachedDataApiService
  ) {
    super(dialogRef);
  }

  ngOnInit(): void {
    this.initContractMailTemplates();
    this.initForm();
    this.observeMailTemplateControl();
  }

  @MarkFormAsTouchedIfInvalid('sendContractForm')
  sendContract(): void {
    if (this.sendContractForm.sendByMail.value && this.sendContractForm.email.invalid) {
      this.sendContractForm.email.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const emailTemplate: GiEmailTemplate = new GiEmailTemplate(
      this.sendContractForm.email.body.value,
      this.sendContractForm.email.cc.value.map(email => email.address),
      this.sendContractForm.email.cci.value.map(email => email.address),
      this.sendContractForm.email.from.value,
      this.emailTemplate().froms.find((from: EmailFrom) => from.email === this.sendContractForm.email.from.value)?.name,
      this.sendContractForm.email.subject.value,
      this.sendContractForm.email.to.value.map(email => email.address),
      this.sendContractForm.email.from.value,
      this.filterSelectedAttachments(this.sendContractForm.email.attachments.controls as ContractEmailAttachmentForm[])
    );

    const selectedContractAttachments: GiEmailAttachment[] = this.filterSelectedAttachments(
      this.sendContractForm.contractAttachments.controls
    );

    const smsTemplate: SmsTemplate = this.sendContractForm.sms.toSmsTemplate();

    this.isLoading.set(true);
    this.sendContractForm.disable();

    this.contractApiService
      .sendContract(
        this.data.contract.id,
        this.data.type,
        false,
        this.sendContractForm.sendByMail.value,
        this.sendContractForm.sendBySms.value,
        emailTemplate,
        smsTemplate,
        selectedContractAttachments
      )
      .pipe(
        tap(() => {
          this.snackbarService.openSuccess(
            `Le contrat a bien été ${this.data.type === SendContractType.signature ? 'envoyé' : 'relancé'}`
          );
          this.dialogRef.close(true);
        }),
        finalize(() => {
          this.isLoading.set(false);
          this.sendContractForm.enable();
        })
      )
      .subscribe();
  }

  initForm(): void {
    this.isLoading.set(true);
    this.sendContractForm.disable();

    this.contractApiService
      .getContractSmsTemplate(this.data.contract.id, this.data.type)
      .pipe(
        tap((smsTemplate: SmsTemplate) => {
          this.initSmsForm(smsTemplate);
          this.initContractAttachments(this.data.contract.files);
        }),
        finalize(() => {
          this.isLoading.set(false);
          this.sendContractForm.enable();
        })
      )
      .subscribe();
  }

  initEmailForm(template: GiEmailTemplate): void {
    if (template?.from) {
      this.sendContractForm.email.from.patchValue(template.from);
    }

    if (template?.to) {
      this.initEmailAddressForm(template.to, this.sendContractForm.email.to);
    }

    if (template?.cci) {
      this.initEmailAddressForm(template.cci, this.sendContractForm.email.cci);
    }

    if (template?.attachments) {
      this.initEmailAttachments(template.attachments);
    }

    if (template?.body) {
      this.sendContractForm.email.body.patchValue(template.body);
    }

    this.sendContractForm.email.subject.patchValue(template?.subject ?? '');
  }

  private filterSelectedAttachments(attachments: ContractEmailAttachmentForm[]): GiEmailAttachment[] {
    return attachments
      .filter((attachment: ContractEmailAttachmentForm) => attachment.isSelected.value)
      .map(
        (attachment: ContractEmailAttachmentForm) =>
          new GiEmailAttachment(
            attachment.id.value,
            attachment.type.value,
            attachment.name.value,
            attachment.content.value
          )
      );
  }

  private initSmsForm(smsTemplate: SmsTemplate): void {
    this.sendContractForm.sms.fromSmsTemplate(smsTemplate);
  }

  private initContractAttachments(files: GIFile[]): void {
    this.sendContractForm.contractAttachments.clear();
    files.forEach((file: GIFile) => {
      const contractEmailAttachmentForm: ContractEmailAttachmentForm = ContractEmailAttachmentForm.fromAttachment(
        new GiEmailAttachment(file.id, file.mimeType, file.name, null, null, file.title, file.type),
        file.type === GIFileType.contract
      );
      this.sendContractForm.contractAttachments.push(contractEmailAttachmentForm);
    });
  }

  private initEmailAttachments(attachements: GiEmailAttachment[]): void {
    this.sendContractForm.email.attachments.clear();
    attachements.forEach((attachment: GiEmailAttachment) => {
      const emailAttachementForm: ContractEmailAttachmentForm = ContractEmailAttachmentForm.fromAttachment(
        attachment,
        false
      );
      this.sendContractForm.email.attachments.push(emailAttachementForm);
    });
  }

  private initContractMailTemplates(): void {
    this.isLoading.set(true);

    this.contractEmailTemplateApiService
      .getEmailTemplates(this.data.partnerId)
      .pipe(
        map((templates: ContractEmailContentTemplate[]) =>
          templates.filter(
            (template: ContractEmailContentTemplate) =>
              !template.archivedAt &&
              template.type ===
                (this.data.type === SendContractType.signature
                  ? ContractMailTemplateType.contractSend
                  : ContractMailTemplateType.contractReminder)
          )
        ),
        tap((templates: ContractEmailContentTemplate[]) => {
          this.contractMailTemplates.set(templates);
          const defaultTemplate: ContractEmailContentTemplate = templates.find(
            (template: ContractEmailContentTemplate) => template.isDefault
          );
          if (defaultTemplate) {
            this.sendContractForm.mailTemplateId.patchValue(defaultTemplate.id);
          }
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  private observeMailTemplateControl(): void {
    this.subscription.add(
      FormHelper.getValueChange(this.sendContractForm.mailTemplateId)
        .pipe(
          filter((mailTemplateId: number) => !!mailTemplateId),
          tap(() => this.isLoading.set(true)),
          switchMap((mailTemplateId: number) =>
            this.contractApiService.getContractEmailTemplate(this.data.contract.id, mailTemplateId)
          ),
          tap(() => this.isLoading.set(false)),
          tap((emailTemplate: GiEmailTemplate) => {
            this.fromEmails.set(emailTemplate.froms);
            this.initEmailForm(emailTemplate);
            if (!this.emailTemplate()) {
              this.displayTo.set(true);
              this.displayCci.set(true);
              this.displayCc.set(true);
              this.cdr.markForCheck();
            }
            this.emailTemplate.set(emailTemplate);
          })
        )
        .subscribe()
    );
  }
}
